import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { emptyTableCellClassName } from '@/constants/ui'
import type { Order } from '@/types/order'
import type { OrderStatus } from '@/types/enums'
import { LiveTimer } from '@/components/orders/LiveTimer'
import { StatusBadge } from '@/components/orders/StatusBadge'
import { OrderKindBadge } from '@/components/orders/OrderKindBadge'
import { formatMoscowDateTime, isOverdue } from '@/utils/format'
import { formatLicensePlate } from '@/utils/licensePlate'
import { profileName } from '@/utils/entities'
import { listCompanies } from '@/api/companies'
import { USE_MOCK } from '@/config'
import { LoadingSecondsBanner } from '@/components/ui/LoadingSecondsBanner'
import { Loader } from '@/components/ui/Loader'
import { prefetchOrderDetail } from '@/utils/prefetch'

const terminal = new Set([
  'COMPLETED',
  'CANCELLED',
  'REFUSED_BY_INSPECTOR',
  'REFUSED_BY_SELLER',
  'REFUSED_BUYOUT',
])

export function OrderTable({
  orders,
  loading,
  onRowClick,
}: {
  orders: Order[]
  loading?: boolean
  onRowClick?: (id: string) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'active'],
    queryFn: () => listCompanies({ pageSize: 200, type: 'all' }),
  })
  const companies = companiesData?.items ?? []
  function companyDisplayName(o: Order, kind: 'customer' | 'contractor') {
    const embedded =
      kind === 'customer' ? o.customer_company : o.contractor_company
    if (embedded?.name) return embedded.name
    const id =
      kind === 'customer' ? o.customer_company_id : o.contractor_company_id
    if (!id) return '—'
    return companies.find((c) => c.id === id)?.name ?? (USE_MOCK ? id : '—')
  }
  function managerDisplay(o: Order) {
    if (o.manager?.full_name) return o.manager.full_name
    if (USE_MOCK) return profileName(o.manager_id)
    return o.manager_id ? '—' : '—'
  }
  function inspectorDisplay(o: Order) {
    if (!o.inspector_id) return '—'
    if (o.inspector_profile?.full_name) return o.inspector_profile.full_name
    if (USE_MOCK) return profileName(o.inspector_id)
    return '—'
  }
  const isInitialLoading = Boolean(loading) && orders.length === 0
  const isBackgroundLoading = Boolean(loading) && orders.length > 0
  return (
    <div className="overflow-x-auto rounded-xl border border-[var(--stryzh-color-border-default)]">
      {isBackgroundLoading ? <LoadingSecondsBanner /> : null}
      <table className="w-full min-w-[860px] border-collapse text-left text-sm">
        <thead className="sticky top-0 z-10 bg-[var(--stryzh-color-surface)] text-[var(--stryzh-color-text-secondary)]">
          <tr>
            <th className="px-3 py-2 font-medium">{t('orders.table.date')}</th>
            <th className="px-3 py-2 font-medium">{t('orders.table.vehicle')}</th>
            <th className="px-3 py-2 font-medium">{t('orders.table.status')}</th>
            <th className="px-3 py-2 font-medium">{t('orders.table.customerManager')}</th>
            <th className="px-3 py-2 font-medium">{t('orders.table.contractorInspector')}</th>
            <th className="px-3 py-2 font-medium">{t('orders.table.address')}</th>
            <th className="px-3 py-2 font-medium">{t('orders.table.inStatus')}</th>
          </tr>
        </thead>
        <tbody>
          {isInitialLoading ? (
            <tr>
              <td colSpan={7} className={emptyTableCellClassName}>
                <Loader centered size="sm" />
              </td>
            </tr>
          ) : orders.length === 0 ? (
            <tr>
              <td colSpan={7} className={emptyTableCellClassName}>
                {t('common.noData')}
              </td>
            </tr>
          ) : (
            orders.map((o) => {
              const arch = terminal.has(o.status)
              const overdue = isOverdue(
                o.status,
                o.created_at,
                o.status_updated_at,
              )
              return (
                <tr
                  key={o.id}
                  className={`cursor-pointer border-t border-[color:var(--stryzh-color-border-default)] hover:bg-[color:var(--stryzh-color-border-subtle)] ${
                    arch ? 'opacity-[var(--stryzh-opacity-archived)]' : ''
                  } ${overdue && !arch ? 'bg-[color:var(--stryzh-color-order-card-timeout-bg)]' : ''}`}
                  onMouseEnter={() => {
                    qc.setQueryData(['order', o.id], o)
                    prefetchOrderDetail(qc, o.id)
                  }}
                  onFocus={() => {
                    qc.setQueryData(['order', o.id], o)
                    prefetchOrderDetail(qc, o.id)
                  }}
                  onClick={() => onRowClick?.(o.id)}
                >
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatMoscowDateTime(o.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[color:var(--stryzh-color-accent-orange)]">
                        {o.brand} {o.model}
                      </span>
                      <OrderKindBadge kind={o.customer_kind} />
                    </div>
                    <div className="mt-0.5 font-[family-name:var(--stryzh-font-family-mono)] text-xs text-[var(--stryzh-color-text-secondary)]">
                      № {o.id.slice(0, 8).toUpperCase()}
                      {o.vin ? ` · VIN ${o.vin}` : ''}
                    </div>
                    {(o.vehicle_year || o.license_plate) && (
                      <div className="mt-0.5 font-[family-name:var(--stryzh-font-family-mono)] text-xs text-[var(--stryzh-color-text-secondary)]">
                        {[
                          o.vehicle_year ? `${o.vehicle_year}` : null,
                          o.license_plate ? formatLicensePlate(o.license_plate) : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={o.status as OrderStatus} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    {o.customer_kind === 'individual' ? (
                      <>
                        <div className="whitespace-nowrap">{o.individual_customer_name ?? '—'}</div>
                        <div className="mt-0.5 text-xs text-[var(--stryzh-color-text-secondary)]">{t('orders.table.individual')}</div>
                      </>
                    ) : (
                      <>
                        <div className="whitespace-nowrap">{companyDisplayName(o, 'customer')}</div>
                        <div className="mt-0.5 text-xs text-[var(--stryzh-color-text-secondary)]">
                          {managerDisplay(o)}
                        </div>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="whitespace-nowrap">{companyDisplayName(o, 'contractor')}</div>
                    <div className="mt-0.5 text-xs text-[var(--stryzh-color-text-secondary)]">
                      {o.inspector_id ? inspectorDisplay(o) : t('orders.table.notAssigned')}
                    </div>
                  </td>
                  <td
                    className="max-w-[200px] truncate px-3 py-2"
                    title={o.address}
                  >
                    {o.address}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-[var(--stryzh-color-text-secondary)]">
                    {arch ? (
                      formatMoscowDateTime(o.status_updated_at ?? o.created_at)
                    ) : (
                      <LiveTimer
                        status={o.status as OrderStatus}
                        createdAt={o.created_at}
                        statusUpdatedAt={o.status_updated_at}
                      />
                    )}
                  </td>
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
